import { useState, useEffect, useRef } from 'react';
import * as unifiedAlertsService from 'service/unifiedAlerts';
import { FilterStateAzure } from '../AdvancedFilterDrawer/types';
import { DEFAULT_FILTERS_AZURE } from '../AdvancedFilterDrawer/constants';
import { timeRangeToDates } from '../unifiedAlertsUtils';
import { loadDashboardFilters, saveDashboardFilters } from 'service/service';

const DEFAULT_FILTER_OPTIONS = {
  severity: ['Critical', 'Major', 'Minor', 'Warning', 'Information'],
  status: ['Fired','Resolved'],
  
};

export const expandSectorFilterFlat = (
  params: Record<string, any>,
  filterOptions: any
) => {
  const sectorMap = filterOptions?.sectorDropdownValues || {};

  const selectedSector = params?.sector;

  if (!selectedSector) {
    return params;
  }

  const sectorList = Array.isArray(selectedSector)
    ? selectedSector
    : String(selectedSector)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  const expandedList = sectorList.flatMap((sector) => {
    const mapped = sectorMap[sector];

    if (Array.isArray(mapped)) {
      return mapped;
    }

    return mapped || sector;
  });

  const uniqueList = expandedList.filter(
    (item, index, arr) => arr.indexOf(item) === index
  );

  return {
    ...params,
    sector: uniqueList.join(", "),
  };
};

export const useUnifiedAlertsAzure = () => {
  const [activeTile, setActiveTile] = useState('Critical');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [simpleSearchText, setSimpleSearchText] = useState('');
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [appliedFilters, setAppliedFilters] = useState<FilterStateAzure>(DEFAULT_FILTERS_AZURE);
  const [filterOptions, setFilterOptions] = useState<any>(DEFAULT_FILTER_OPTIONS);
  const [summary, setSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isFiltersHydrated, setIsFiltersHydrated] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
    return saved ? Number(saved) : 5;
  });
  const [fixedStartDate, setFixedStartDate] = useState<string|undefined>(undefined);
  const [fixedEndDate, setFixedEndDate] = useState<string|undefined>(undefined);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const persistDateRangeToSession = (filters: FilterStateAzure) => {
    if (filters.timeRange) {
      sessionStorage.setItem('unifiedAlertsDateRangeType', filters.timeRange);
    }

    if (filters.timeRange === 'custom' && filters.startDate && filters.endDate) {
      sessionStorage.setItem('unifiedAlertsStartDate', filters.startDate);
      sessionStorage.setItem('unifiedAlertsEndDate', filters.endDate);
    } else {
      sessionStorage.removeItem('unifiedAlertsStartDate');
      sessionStorage.removeItem('unifiedAlertsEndDate');
    }
    window.dispatchEvent(new Event('unifiedAlertsDateRangeHydrated'));
  };

  const withCustomDates = (filters: FilterStateAzure): FilterStateAzure => {
    if (filters.timeRange !== 'custom') return filters;

    return {
      ...filters,
      startDate: filters.startDate ?? sessionStorage.getItem('unifiedAlertsStartDate') ?? undefined,
      endDate: filters.endDate ?? sessionStorage.getItem('unifiedAlertsEndDate') ?? undefined,
    };
  };

  const getDateParams = () => {
  const pewsType = sessionStorage.getItem('unifiedAlertsDateRangeType');
  if (pewsType) {
    if (pewsType === 'custom') {
      const start = sessionStorage.getItem('unifiedAlertsStartDate');
      const end = sessionStorage.getItem('unifiedAlertsEndDate');
      if (start && end) return { startDate: start, endDate: end };
    } else {
      const now = new Date();
      const endDate = now.toISOString();
      const parseValue = (v: string) => {
        const match = v.match(/^(\d+)\s*(m|min|minutes|h|hr|hour|d|days|day|w|weeks|week)$/i);
        if (!match) return null;
        const num = parseInt(match[1]);
        const unit = match[2].toLowerCase();
        
        let multiplier = 1;
        if (unit.startsWith('m') && !unit.startsWith('mi')) multiplier = 60 * 1000;
        else if (unit.startsWith('mi')) multiplier = 60 * 1000;
        else if (unit.startsWith('h')) multiplier = 60 * 60 * 1000;
        else if (unit.startsWith('d')) multiplier = 24 * 60 * 60 * 1000;
        else if (unit.startsWith('w')) multiplier = 7 * 24 * 60 * 60 * 1000;
        
        return num * multiplier;
      };
      const rangeMap: Record<string, number> = {
        '5m':  5 * 60 * 1000,
        '10m': 10 * 60 * 1000,
        '15m': 15 * 60 * 1000,
        '30m': 30 * 60 * 1000,
        '1h':  60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '7d':  7  * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000,
        '90d': 90 * 24 * 60 * 60 * 1000,
        'today': now.getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(),
      };
      const offset = rangeMap[pewsType] ?? parseValue(pewsType);
      if (offset !== undefined && offset !== null) {
        return {
          startDate: new Date(now.getTime() - offset).toISOString(),
          endDate,
        };
      }
    }
  }
  return timeRangeToDates(appliedFilters.timeRange);
};
useEffect(() => {
  const handleDateRangeChanged = () => {
    loadAll(getDateParams());
    loadAlerts(getDateParams());
  };

  window.addEventListener('unifiedAlertsDateRangeChanged', handleDateRangeChanged);
  return () => window.removeEventListener('unifiedAlertsDateRangeChanged', handleDateRangeChanged);
}, [page, pageSize, searchText, activeTile, sortingInfo, appliedFilters]);

// useEffect(() => {
//   const handleDateRangeChanged = () => {
//     loadAll(getDateParams());
//     loadAlerts(getDateParams());
//   };

//   window.addEventListener('unifiedAlertsDateRangeChanged', handleDateRangeChanged);
//   return () => window.removeEventListener('unifiedAlertsDateRangeChanged', handleDateRangeChanged);
// }, [page, pageSize, searchText, activeTile, sortingInfo, appliedFilters]);

  const loadSummary = async (dateParams?: { startDate: string; endDate: string }) => {
    try {
      setSummaryLoading(true)
      const params:any = dateParams ?? getDateParams();
      params.search = searchText || undefined;
      applyAdvanceFilters(params)
      const updatedParams = expandSectorFilterFlat(params, filterOptions);
      const data = await unifiedAlertsService.getSummaryAzure(updatedParams);
      setSummary(data);
      setSummaryLoading(false)
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const applyAdvanceFilters = (params: any) => {
    if (appliedFilters.mappedSeverity.length > 0) params.severity = appliedFilters.mappedSeverity.map((s: any) => s.value === 'Information' ? 'OK' : s.value).join(',');
    if (appliedFilters.status.length > 0) params.status = appliedFilters.status.map((s: any) => s.value).join(',');
    if (appliedFilters.subscriptions.length > 0) params.subscriptions = appliedFilters.subscriptions.map((s: any) => s.value).join(',');
    if (appliedFilters.resourceGroups.length > 0) params.resourceGroups = appliedFilters.resourceGroups.map((s: any) => s.value).join(',');
    if (appliedFilters.resourceTypes.length > 0) params.resourceTypes = appliedFilters.resourceTypes.map((s: any) => s.value).join(',');
    if (appliedFilters.signalTypes.length > 0) params.signalTypes = appliedFilters.signalTypes.map((s: any) => s.value).join(',');
    if (appliedFilters.monitoringServices.length > 0) params.monitoringServices = appliedFilters.monitoringServices.map((s: any) => s.value).join(',');
    
    if (sortingInfo.sortBy && sortingInfo.sortDir) {
      params.sortBy = sortingInfo.sortBy;
      params.sortOrder = sortingInfo.sortDir;
    }
  }

  const loadAlerts = async (dateParams?: { startDate: string; endDate: string }) => {
    setIsLoading(true);
    try {
      const params: any = {
        page,
        pageSize,
        search: searchText || undefined,
        severity: activeTile,
        startDate: dateParams?.startDate || fixedStartDate || getDateParams().startDate,
        endDate: dateParams?.endDate || fixedEndDate || getDateParams().endDate
      };

      applyAdvanceFilters(params)
      if(activeTile !== 'Closed'){
        params.severity = activeTile === 'Information' ? 'OK' : activeTile;
      }else{
        params.status = 'Resolved'
      }
      const updatedParams = expandSectorFilterFlat(params, filterOptions);
      const response = await unifiedAlertsService.getAlertsAzure(updatedParams);
      setAlerts(response.data);
      setTotalAlerts(response.pagination.total);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };


  const loadAllDropDowns = async () => {
    try {
      const data = await unifiedAlertsService.getDropDownDataAzure();
      setFilterOptions({...DEFAULT_FILTER_OPTIONS, ...data});

      setAppliedFilters(prev => ({
        ...prev,
      }));
    } catch (error) {
      console.error('Error loading most impacted apps:', error);
    }
  };

  const loadAll = (dateParams?: { startDate: string; endDate: string }) => {
    setFixedStartDate(dateParams?.startDate)
    setFixedEndDate(dateParams?.endDate)
    loadSummary(dateParams);
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
      if (saved !== null) setAutoRefreshInterval(saved === '0' ? null : Number(saved));
    };

    window.addEventListener('storage', handleStorageChange);
    const checkInterval = setInterval(() => {
      const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
      const current = autoRefreshInterval === null ? 0 : autoRefreshInterval;
      if (saved !== null && Number(saved) !== current) {
        setAutoRefreshInterval(saved === '0' ? null : Number(saved));
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkInterval);
    };
  }, [autoRefreshInterval]);

  useEffect(() => {
    const handleAlertsSearch = (event: any) => {
      const searchText = event.detail?.searchText || '';
      setSimpleSearchText(searchText);
      setSearchText(searchText);
      setPage(1);
    };

    window.addEventListener('alertsSearch', handleAlertsSearch);
    return () => window.removeEventListener('alertsSearch', handleAlertsSearch);
  }, []);

  useEffect(() => {
    loadAll();
    loadAllDropDowns();
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [page, pageSize, activeTile, sortingInfo]);

  useEffect(() => {
    const loadSavedFilters = async () => {
      try {
        const savedFilters = await loadDashboardFilters({
          dashboardId:  'azure',
          dashboardName: 'Azure Alerts Dashboard',
          pageName: 'Azure Alerts',
        });

        if (savedFilters?.filters) {
          const { savedActiveTile } = savedFilters?.meta_data || {};

          const hydratedFilters = savedFilters.filters as FilterStateAzure;

          setAppliedFilters(hydratedFilters);
          setActiveTile(savedActiveTile || 'Critical');

          persistDateRangeToSession(hydratedFilters);

          window.dispatchEvent(new Event('unifiedAlertsDateRangeHydrated'));
        } else {
          setAppliedFilters(DEFAULT_FILTERS_AZURE);
          setActiveTile('Critical');
        }
      } catch (e) {
        console.error('Failed to load saved filters', e);
      } finally {
        setIsFiltersHydrated(true);
      }
    };

    loadSavedFilters();
  }, []);

    useEffect(() => {
    if (!isFiltersHydrated) return;

    loadAll(getDateParams());
    loadAlerts(getDateParams());
  }, [isFiltersHydrated]);

  useEffect(() => {
     if (!isFiltersHydrated) return;
    
    loadAll(getDateParams());
    loadAlerts(getDateParams());
  },[appliedFilters, searchText])

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefreshInterval) {
      intervalRef.current = setInterval(() => {
        loadAll(getDateParams());
        loadAlerts(getDateParams());
      }, autoRefreshInterval * 60 * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefreshInterval, page, pageSize, searchText, activeTile, sortingInfo, appliedFilters]);

  const handleTileClick = (key: string) => {
    setActiveTile(key);
    setPage(1);
  };

  const handleApplyFilters = (filters: FilterStateAzure) => {
    const filtersToApply = withCustomDates(filters);

    setAppliedFilters(filtersToApply);
    persistDateRangeToSession(filtersToApply);
    setPage(1);
  };

  const handleSaveDefaultFilters = async(filters: FilterStateAzure) => {
    const filtersToSave = withCustomDates(filters);

    setAppliedFilters(filtersToSave);
    persistDateRangeToSession(filtersToSave);

    await saveDashboardFilters({
      dashboardId:  'azure',
      dashboardName: 'Azure Alerts Dashboard',
      pageName: 'Azure Alerts',
      meta_data: {
        savedActiveTile: activeTile,
      }
    }, filtersToSave).catch(() => {});

    setPage(1);
  };

  const handleResetDefaultFilters = async(filters: FilterStateAzure) => {
    setAppliedFilters(filters);

    persistDateRangeToSession(filters);

    setActiveTile('Critical');

    await saveDashboardFilters({
      dashboardId:  'azure',
      dashboardName: 'Azure Alerts Dashboard',
      pageName: 'Azure Alerts',
      meta_data: {
        savedActiveTile: 'Critical',
      }
    }, filters).catch(() => {});

    setPage(1);
  };


  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      const params: any = {
        page: 1,
        pageSize: 10000,
        search: searchText || undefined,
        severity: activeTile,
      };

      if (appliedFilters.mappedSeverity.length > 0) params.severity = appliedFilters.mappedSeverity.map((s: any) => s.value).join(',');
      if (appliedFilters.status.length > 0) params.status = appliedFilters.status.map((s: any) => s.value).join(',');
     
      if (appliedFilters.timeRange) {
        const { startDate, endDate } = getDateParams();
        params.startDate = startDate;
        params.endDate = endDate;
      }
      if (sortingInfo.sortBy && sortingInfo.sortDir) {
        params.sortBy = sortingInfo.sortBy;
        params.sortOrder = sortingInfo.sortDir;
      }

      const updatedParams = expandSectorFilterFlat(params, filterOptions);
      await Promise.all([unifiedAlertsService.exportAlertsAzure(updatedParams), minDelay]);
    } catch (error) {
      console.error('Error exporting alerts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return {
    activeTile,
    page,
    pageSize,
    searchText,
    simpleSearchText,
    sortingInfo,
    appliedFilters,
    summary,
    alerts,
    totalAlerts,
    isLoading,
    isExporting,
    filterOptions,
    fixedStartDate,
    fixedEndDate,
    setFixedStartDate,
    setFixedEndDate,
    setPage,
    setPageSize,
    setSearchText,
    setSimpleSearchText,
    setSortingInfo,
    handleTileClick,
    handleApplyFilters,
    handleSaveDefaultFilters,
    handleResetDefaultFilters,
    handleExport,
    summaryLoading
  };
};